import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import type { ClientActivity } from '@freelancer-os/shared-types';
import { ActivityItem } from './ActivityItem';
import { useClientActivities } from '../../../hooks/useClients';

interface ActivityTabProps {
  clientId: string;
  initialActivities: ClientActivity[];
}

const PAGE_SIZE = 50;

interface PageState {
  appended: ClientActivity[];
  hasMore: boolean;
  pendingBefore: string | null;
}

export function ActivityTab({ clientId, initialActivities }: ActivityTabProps) {
  const { t } = useTranslation('clients');
  const [pageState, setPageState] = useState<PageState>({
    appended: [],
    hasMore: initialActivities.length === PAGE_SIZE,
    pendingBefore: null,
  });

  const activitiesQuery = useClientActivities(
    pageState.pendingBefore ? clientId : null,
    { limit: PAGE_SIZE, before: pageState.pendingBefore ?? undefined },
  );

  // When a page fetch resolves, append the new batch and reset the trigger.
  // The setState here is intentional — TanStack Query data arrival is the
  // signal we react to; alternative would require a more complex query setup.
  useEffect(() => {
    if (!pageState.pendingBefore || !activitiesQuery.data || activitiesQuery.isFetching) return;
    const batch = activitiesQuery.data;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPageState((prev) => {
      const known = new Set(prev.appended.map((a) => a.id));
      const fresh = batch.filter((a) => !known.has(a.id));
      return {
        appended: fresh.length > 0 ? [...prev.appended, ...fresh] : prev.appended,
        hasMore: batch.length === PAGE_SIZE,
        pendingBefore: null,
      };
    });
  }, [pageState.pendingBefore, activitiesQuery.data, activitiesQuery.isFetching]);

  const all = [...initialActivities, ...pageState.appended];

  if (all.length === 0) {
    return <p className="text-sm text-surface-500 text-center py-8">{t('activity.empty')}</p>;
  }

  const loadMore = () => {
    const last = all[all.length - 1];
    if (last) setPageState((prev) => ({ ...prev, pendingBefore: last.createdAt }));
  };

  return (
    <div className="border border-surface-200 rounded-md bg-surface-0 px-4">
      <div>
        {all.map((activity) => (
          <ActivityItem key={activity.id} activity={activity} />
        ))}
      </div>
      {pageState.hasMore && (
        <div className="py-3 text-center border-t border-surface-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={loadMore}
            disabled={activitiesQuery.isFetching}
          >
            {activitiesQuery.isFetching ? t('activity.loading') : t('activity.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
